// Deriv WebSocket helper — supports Deriv's new architecture
// (developers.deriv.com/llms.txt, Feb 2026):
//   • Public stream:    wss://api.derivws.com/trading/v1/options/ws/public
//   • Authenticated:    wss://api.derivws.com/trading/v1/options/ws/{demo|real}?otp=<token>
//
// We keep a SINGLE active socket. It starts on the public stream and is upgraded
// to the authenticated stream (with OTP) once the user logs in via OAuth and the
// backend returns the WS URL via /api/deriv/otp/{account_id}.

import { api } from "@/lib/api";
import { requireEmail } from "@/lib/emailGate";

let socket = null;
let urlInUse = null;
let reqId = 1;
const pending = new Map();      // reqId -> { resolve, reject }
const subscribers = new Map();  // reqId -> handler (for streaming msgs)
const subServerIds = new Map(); // reqId -> Deriv subscription.id (for server-side forget)
const queue = [];               // outbound queue while WS isn't open yet
let opening = false;
let onConnState = null;         // optional callback (state) => void

// ── Connection stability (v8.9.130) ───────────────────────────────────────
// Two problems caused the "Auto-FX keeps connecting and disconnecting" report:
//   1. No keepalive. Browser WebSocket clients CAN'T send native ping frames,
//      and Deriv (plus any proxy/load-balancer in the path, typically 60-100s
//      idle) closes a socket that's been quiet. During a long Auto-FX run the
//      balance/portfolio polls aren't frequent enough to guarantee the socket
//      never goes idle, so it would silently die mid-run.
//   2. No proactive reconnect. When the socket died we only reopened it lazily
//      on the NEXT send() — leaving a visible disconnected window where balance
//      / live trades blinked out.
// Fix: send an application-level {ping:1} every 25s while open, and the moment
// an *unexpected* close happens (i.e. not our own URL swap / teardown) for an
// authenticated session, transparently reconnect on a fresh OTP with backoff.
let pingTimer = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let closingIntentionally = false;   // true while WE tear down the socket on purpose
const PING_INTERVAL_MS = 25000;     // 25s heartbeat — well under the 60s proxy idle floor
const MAX_RECONNECT_DELAY_MS = 30000;

const stopPing = () => {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
};
const startPing = () => {
  stopPing();
  pingTimer = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ ping: 1, req_id: reqId++ }));
      } catch {
        /* ignore — onclose will handle a genuinely dead socket */
      }
    }
  }, PING_INTERVAL_MS);
};
const cancelReconnect = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

// Auto-reconnect config — when the socket dies and we have an authenticated
// session, deriv.js will transparently fetch a FRESH OTP URL from the backend
// (Deriv OTP URLs are short-lived, ~1hr) and reopen. Without this, long bot
// runs would die with "WS open timeout" after the OTP expired.
let authCtx = null; // { accountId, accessToken }
const isOtpUrl = (u) => typeof u === "string" && /\/trading\/v1\/options\/ws\//.test(u);
const refreshOtpUrl = async () => {
  if (!authCtx?.accountId || !authCtx?.accessToken) return null;
  try {
    const res = await api.post(
      `/deriv/otp/${encodeURIComponent(authCtx.accountId)}`,
      {},
      { headers: { Authorization: `Bearer ${authCtx.accessToken}` } }
    );
    const payload = res?.data?.data || res?.data || {};
    return payload?.url || payload?.ws_url || payload?.websocket_url || null;
  } catch {
    return null;
  }
};

// v8.9.129 — Silent background OTP refresh. Deriv OTP URLs live ~1 hour.
// Every 45 min while we have an active authenticated session, transparently
// fetch a fresh OTP URL and swap the live socket onto it. Result: long bot
// runs (Auto-FX, Auto-Trade overnight) never see "WS open timeout" because
// they're always running on a fresh OTP.
//
// Why 45 min: Deriv's documented OTP TTL is ~1 hour; 45 min gives us 15 min
// of slack. Even on the unlikely chance Deriv shortens the TTL, we'll catch
// it before it bites.
const OTP_REFRESH_INTERVAL_MS = 45 * 60 * 1000;   // 45 min
let otpRefreshTimer = null;
const startOtpRefreshLoop = () => {
  if (otpRefreshTimer) return;        // already running — idempotent
  otpRefreshTimer = setInterval(async () => {
    if (!authCtx?.accountId || !authCtx?.accessToken) {
      // No active session — nothing to refresh.
      stopOtpRefreshLoop();
      return;
    }
    const fresh = await refreshOtpUrl();
    if (!fresh) return;               // backend hiccup — try again next cycle
    try {
      // Swap the socket onto the new URL. ensureSocket tears down the old
      // one and opens the new — no lost subscriptions because the
      // queue/pending logic re-issues anything that was in-flight.
      await ensureSocket(fresh);
    } catch {
      /* leave the old socket up; we'll retry on the next interval */
    }
  }, OTP_REFRESH_INTERVAL_MS);
};
const stopOtpRefreshLoop = () => {
  if (otpRefreshTimer) {
    clearInterval(otpRefreshTimer);
    otpRefreshTimer = null;
  }
};

const DEFAULT_PUBLIC_URL =
  "wss://api.derivws.com/trading/v1/options/ws/public";  // v9.4 — legacy v3 now rejects ALL symbols (InvalidSymbol)

// Self-healing reconnect with exponential backoff. Only fires for
// authenticated sessions (we always grab a FRESH OTP first, since OTP URLs
// are short-lived). Falls back to the public WS if the OTP can't be renewed
// so the app keeps streaming ticks instead of sitting dead.
const scheduleReconnect = () => {
  if (reconnectTimer || opening) return;   // already pending / in-flight
  if (!authCtx) return;                     // only self-heal authenticated sessions
  const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** reconnectAttempts);
  reconnectTimer = setTimeout(async () => {
    reconnectTimer = null;
    reconnectAttempts += 1;
    // v8.9.146 — Deriv OTP WS URLs are SINGLE-USE. Reconnecting on the old
    // `urlInUse` (an already-consumed OTP) makes Deriv accept the socket then
    // instantly close it → another onclose → another reconnect = the visible
    // "connects then disconnects back" loop users reported. So we ONLY ever
    // reconnect an authenticated session on a FRESHLY minted OTP. If the mint
    // hiccups, drop to the PUBLIC socket (ticks/charts keep working) and retry
    // the OTP on the next backoff tick — never reuse the stale OTP.
    const fresh = await refreshOtpUrl();
    if (!fresh) {
      try {
        await ensureSocket(DEFAULT_PUBLIC_URL);
      } catch {
        /* ignore — we'll retry below */
      }
      scheduleReconnect();
      return;
    }
    try {
      await ensureSocket(fresh);
    } catch {
      scheduleReconnect();                  // try again with a longer backoff
    }
  }, delay);
};

const setSocketState = (s) => {
  if (typeof onConnState === "function") onConnState(s);
};

const flushQueue = () => {
  while (queue.length && socket && socket.readyState === WebSocket.OPEN) {
    socket.send(queue.shift());
  }
};

const ensureSocket = (url) => {
  // Already open at the right URL? Reuse.
  if (socket && urlInUse === url && socket.readyState === WebSocket.OPEN) {
    return Promise.resolve(socket);
  }
  // Already opening at the right URL? Wait for it.
  if (opening && urlInUse === url) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const t = setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          clearInterval(t);
          resolve(socket);
        } else if (Date.now() - start > 15000) {  // v8.9.128 — 8s → 15s
          clearInterval(t);
          reject(new Error("WS open timeout"));
        }
      }, 100);
    });
  }
  // Otherwise tear down and reconnect at the new URL.
  if (socket) {
    closingIntentionally = true;   // our own teardown — don't let onclose self-reconnect
    try {
      socket.close();
    } catch {
      /* ignore */
    }
    socket = null;
  }
  cancelReconnect();               // we're (re)connecting now — drop any pending backoff
  urlInUse = url;
  opening = true;
  setSocketState("connecting");
  return new Promise((resolve, reject) => {
    const tryOpen = (urlToOpen, allowRefresh) => {
      urlInUse = urlToOpen;
      const s = new WebSocket(urlToOpen);
      const tOpen = setTimeout(async () => {
        try { s.close(); } catch { /* ignore */ }
        // If this was an authenticated (OTP) URL, the OTP almost certainly
        // expired. Ask the backend for a FRESH OTP URL and try one more time.
        if (allowRefresh && isOtpUrl(urlToOpen) && authCtx) {
          const fresh = await refreshOtpUrl();
          if (fresh) {
            tryOpen(fresh, false);
            return;
          }
        }
        // v8.9.128 — final fallback: if the OTP URL refused to open AND
        // refresh failed, try the public WS so the user at least gets
        // ticks/charts. Auth-required calls will return a clean
        // "AuthorizationRequired" error rather than the confusing
        // "WS open timeout" that left the AutoTrade preflight stuck.
        if (allowRefresh && isOtpUrl(urlToOpen)) {
          tryOpen(DEFAULT_PUBLIC_URL, false);
          return;
        }
        opening = false;
        reject(new Error("WS open timeout"));
      }, 15000);   // v8.9.128 — bumped 8s → 15s for slow networks
      s.onopen = () => {
        clearTimeout(tOpen);
        opening = false;
        socket = s;
        closingIntentionally = false;   // a healthy live socket — future closes are "unexpected"
        reconnectAttempts = 0;          // reset backoff after a successful open
        cancelReconnect();
        setSocketState("open");
        startPing();                    // keepalive so Deriv/proxies don't drop an idle socket
        flushQueue();
        resolve(s);
      };
      s.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        const id = msg?.req_id;
        // First reply → resolve pending promise.
        if (id != null && pending.has(id)) {
          const { resolve: resReq } = pending.get(id);
          if (msg?.subscription?.id || subscribers.has(id)) {
            subscribers.set(id, subscribers.get(id) || null);
          }
          pending.delete(id);
          resReq(msg);
        }
        // Stream → forward to handler
        if (id != null && subscribers.has(id)) {
          const handler = subscribers.get(id);
          if (typeof handler === "function") handler(msg);
        }
      };
      s.onerror = () => {
        setSocketState("error");
      };
      s.onclose = () => {
        stopPing();
        // Only clear the module socket if THIS socket is still the active one —
        // an old socket being replaced during a URL swap must not clobber a
        // freshly-opened replacement.
        if (socket === s) socket = null;
        setSocketState("closed");
        // Self-heal: an UNEXPECTED close (idle drop, network blip) on an
        // authenticated session reconnects transparently on a fresh OTP so the
        // bot run / balance / live-trades stream don't sit disconnected.
        if (!closingIntentionally && authCtx) {
          scheduleReconnect();
        }
      };
    };
    tryOpen(url, true);
  });
};

export const onConnectionState = (cb) => {
  onConnState = cb;
};

export const connectPublic = (publicUrl) => {
  // v8.9.129 — if we're moving back to a public-only connection, stop the
  // silent OTP refresh loop (it would just keep failing without authCtx).
  if (!authCtx) stopOtpRefreshLoop();
  return ensureSocket(publicUrl || DEFAULT_PUBLIC_URL);
};

// v8.9.129 — Explicit teardown for signed-out / unmounted state. Called
// by Dashboard's `handleLogout` so we don't leave the OTP refresh timer
// running after the user signs out.
export const teardownAuth = () => {
  authCtx = null;
  closingIntentionally = true;   // signed out — suppress self-healing reconnect
  cancelReconnect();
  stopPing();
  stopOtpRefreshLoop();
};

// Upgrade to an authenticated socket (via OTP URL returned by the backend proxy)
// A valid Deriv login id looks like CR123…, ROT123…, MF123…, VRTC123…, DOT123…
// — letters then digits, no spaces. The OAuth fallback placeholder
// "OAUTH_USER" and any UI label like "DEMO · OAUTH_USER" must NEVER be sent to
// Deriv's OTP endpoint (prod RCA: it 429-stormed Deriv and dropped users to
// demo). This guard makes the frontend refuse those ids.
export const isRealDerivLoginId = (id) =>
  typeof id === "string" && /^[A-Z]{2,4}\d{3,}$/.test(id.trim());

export const connectAuthenticated = async (accountId, accessToken) => {
  if (!isRealDerivLoginId(accountId)) {
    throw new Error(
      `Not a Deriv account id (${accountId}) — staying in demo. Re-link your Deriv account to trade live.`
    );
  }
  // Store auth context so we can transparently refresh expired OTP URLs on
  // reconnect (Deriv OTP URLs typically live ~1 hour).
  authCtx = { accountId, accessToken };
  const res = await api.post(
    `/deriv/otp/${encodeURIComponent(accountId)}`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  // Deriv's new REST returns { data: { url: "wss://..." } }. Axios wraps the
  // body in `res.data`, so the WS URL ends up at `res.data.data.url`. Older
  // response shapes are kept as fallbacks.
  const payload = res?.data?.data || res?.data || {};
  const otpUrl =
    payload?.url ||
    payload?.ws_url ||
    payload?.websocket_url ||
    null;
  if (!otpUrl) {
    throw new Error(
      "Deriv did not return a WebSocket URL — try logging in again or contact support."
    );
  }
  const sock = await ensureSocket(otpUrl);
  // v8.9.129 — kick off the silent 45-min OTP refresh loop now that we're
  // authenticated. Subsequent calls are idempotent (no-op if already
  // running), so this is safe even if connectAuthenticated runs again on
  // an account switch.
  startOtpRefreshLoop();
  return sock;
};

// Make sure a socket is up. If we're on an OTP URL and the socket is dead,
// grab a fresh OTP first so we don't burn the 8s timeout on an expired URL.
// v8.9.128 — if OTP refresh fails AND the public WS isn't already in use,
// fall back to the public WS so unauthenticated calls (ticks/charts) still
// work. Previously the user would just see "WS open timeout" forever.
const ensureUpForSend = async () => {
  if (socket && socket.readyState === WebSocket.OPEN) return;
  if (isOtpUrl(urlInUse) && authCtx) {
    const fresh = await refreshOtpUrl();
    if (fresh) {
      await ensureSocket(fresh);
      return;
    }
    // OTP refresh failed — token is dead. Fall back to the public WS so
    // tick streams + non-auth calls still work. The user will see a
    // proper "AuthorizationRequired" error on auth-required calls (clear
    // signal to re-login) instead of a confusing "WS open timeout".
    urlInUse = null;
  }
  try {
    await ensureSocket(urlInUse || DEFAULT_PUBLIC_URL);
  } catch (e) {
    // Last-ditch fallback: even if the current URL won't open, try the
    // public WS one more time so the rest of the app doesn't lock up.
    if (urlInUse && urlInUse !== DEFAULT_PUBLIC_URL) {
      urlInUse = null;
      await ensureSocket(DEFAULT_PUBLIC_URL);
      return;
    }
    throw e;
  }
};

/* 🚦 Deriv rate-limits `proposal` calls per connection — enforce a min gap
   and auto-retry on "rate limit" so plays recover instead of failing. */
let _lastProposal = 0;
let _rlUntil = 0; // after a rate-limit sighting, widen the gap for a while
const _proposalGate = async () => {
  const gap = Date.now() < _rlUntil ? 2000 : 500;
  const wait = _lastProposal + gap - Date.now();
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  _lastProposal = Date.now();
};

const _wsRequest = async (request, opts = {}) => {
  const id = reqId++;
  const payload = { ...request, req_id: id };
  const str = JSON.stringify(payload);
  await ensureUpForSend();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error("WS request timeout"));
    }, opts.timeout || 15000);
    pending.set(id, {
      resolve: (m) => {
        clearTimeout(timer);
        resolve(m);
      },
      reject,
    });
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(str);
    else queue.push(str);
  });
};

export const send = async (request, opts = {}) => {
  // 🔒 email hard gate — no contract purchase without an email on file
  if (request && request.buy && !requireEmail()) {
    return { error: { code: "EmailRequired", message: "Add your email to continue — one time only" } };
  }
  const isProp = !!(request && request.proposal);
  if (isProp) await _proposalGate();
  let res = await _wsRequest(request, opts);
  // ⏳ rate-limited proposal? breathe and retry instead of surfacing the error
  for (let a = 0; a < 3 && isProp && res?.error && /rate limit/i.test(res.error.message || ""); a += 1) {
    _rlUntil = Date.now() + 30000;
    await new Promise((r) => setTimeout(r, 2000 + a * 2500));
    await _proposalGate();
    res = await _wsRequest(request, opts);
  }
  return res;
};

export const subscribe = async (request, handler) => {
  if (request && request.proposal) await _proposalGate();
  const id = reqId++;
  const payload = { ...request, req_id: id, subscribe: 1 };
  const str = JSON.stringify(payload);
  await ensureUpForSend();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error("WS subscribe timeout"));
    }, 15000);
    pending.set(id, {
      resolve: (m) => {
        clearTimeout(timer);
        subscribers.set(id, handler);
        if (m?.subscription?.id) subServerIds.set(id, m.subscription.id);
        resolve({ subscription_id: id, first: m });
      },
      reject,
    });
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(str);
    else queue.push(str);
  });
};

export const forget = (subscriptionId) => {
  if (subscribers.has(subscriptionId)) subscribers.delete(subscriptionId);
  // Deriv expects its OWN subscription.id (a string) for `forget` — NOT our
  // local req_id. Using the wrong id left streams alive server-side, so
  // re-subscribing to the same symbol/granularity returned AlreadySubscribed
  // (no candles) → blank chart on switch. Send the real id.
  const serverId = subServerIds.get(subscriptionId);
  subServerIds.delete(subscriptionId);
  if (serverId && socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ forget: serverId, req_id: reqId++ }));
    } catch {
      /* ignore */
    }
  }
};

// Convenience wrapper used by older callers: subscribe to a symbol's ticks.
// Returns a teardown function (sync API for backward compat).
export const subscribeTicks = (symbol, handler) => {
  let subId = null;
  let cancelled = false;
  subscribe({ ticks: symbol }, handler)
    .then(({ subscription_id }) => {
      if (cancelled) {
        forget(subscription_id);
      } else {
        subId = subscription_id;
      }
    })
    .catch(() => {
      /* ignore — caller can re-subscribe */
    });
  return () => {
    cancelled = true;
    if (subId != null) forget(subId);
  };
};

// Authorize the current socket with an API token. Used by the
// "Connect with API token" flow — users can generate a token at
// app.deriv.com/account/api-token and paste it into VTS.
export const authorize = async (token) => {
  if (!token) return { error: { code: "MISSING_TOKEN", message: "No token provided" } };
  await ensureSocket(urlInUse || DEFAULT_PUBLIC_URL);
  try {
    const res = await send({ authorize: token });
    return res || { error: { code: "UNKNOWN", message: "No response" } };
  } catch (e) {
    return { error: { code: "WS_ERROR", message: e.message || String(e) } };
  }
};

// Kept for backward compat with the legacy WS URL builder
export const setAppId = (_appId) => {
  /* no-op under the new Deriv API */
};
